import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';

const articleBySlug: Record<string, (typeof articles)[0]> = {};
articles.forEach((a) => { articleBySlug[a.slug] = a; });

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleBySlug[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-900 mb-4">Articolo non trovato</h1>
          <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer whitespace-nowrap">
            Torna alla Home
          </Link>
        </div>
      </div>
    );
  }

  const faqSection = article.sections.find((s) => s.isFAQ);
  const contentSections = article.sections.filter((s) => !s.isFAQ);
  const articleUrl = `https://itaobuyspread.click/articolo/${article.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://itaobuyspread.click/' },
      { '@type': 'ListItem', position: 2, name: 'Guide', item: 'https://itaobuyspread.click/' },
      { '@type': 'ListItem', position: 3, name: article.tagLabel, item: articleUrl },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: article.author },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    image: article.imageUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
      },
    },
  };

  const faqSchemaObj = faqSection?.faqs ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSection.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  } : null;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      {faqSchemaObj && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchemaObj) }} />
      )}

      <article className="min-h-screen bg-background-50">
        <header className="py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li><Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">Home</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li><Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">Guide</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.tagLabel}</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 mb-6">
              <span className="text-xs font-medium text-accent-700 uppercase tracking-wider">{article.tagLabel}</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>

            <p className="text-base md:text-lg text-foreground-600 max-w-2xl leading-relaxed mb-6">
              {article.description}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Aggiornato: {article.dateModified}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                Tempo di lettura: {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
          <div className="relative rounded-2xl overflow-hidden mb-12" style={{ aspectRatio: '16/7' }}>
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {contentSections.map((section, idx) => (
            <section key={idx} className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-5">
                {section.heading}
              </h2>
              <div className="text-base text-foreground-700 leading-relaxed space-y-4">
                {section.content.split('\n\n').filter(Boolean).map((paragraph, pIdx) => (
                  <p key={pIdx}>{paragraph.trim()}</p>
                ))}
              </div>

              {(idx === 1) && (
                <div className="my-8 p-6 rounded-2xl bg-accent-50 border border-accent-200">
                  <p className="text-sm font-semibold text-accent-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    Vuoi provare Itaobuy Spreadsheet subito?
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Torna alla Home per esplorare tutte le categorie
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </section>
          ))}

          {faqSection && faqSection.faqs && (
            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8">
                Domande Frequenti
              </h2>
              <div className="space-y-4">
                {faqSection.faqs.map((faq, idx) => (
                  <details key={idx} className="group rounded-xl bg-background-100 border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-foreground-800 font-medium text-sm md:text-base">
                      {faq.question}
                      <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          <div className="mt-16 p-8 rounded-2xl bg-primary-500 text-center">
            <h3 className="font-heading text-2xl font-semibold text-background-50 mb-3">
              Pronto a Iniziare con Itaobuy Spreadsheet?
            </h3>
            <p className="text-sm text-background-50/80 mb-6 max-w-md mx-auto leading-relaxed">
              Accedi ora allo spreadsheet completo e scopri tutte le categorie di prodotti organizzati.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Itaobuy Sheet
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-8 py-3 rounded-full border-2 border-background-50/50 text-background-50 text-sm font-semibold hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Torna alla Home
                <i className="ri-home-line ml-2"></i>
              </Link>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200 text-center">
            <p className="text-sm text-foreground-500 mb-4">
              Questo articolo fa parte della guida completa di{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer">
                Itaobuy Spreadsheet Italia
              </Link>
            </p>
            <nav className="flex items-center justify-center gap-6 flex-wrap">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>
                Torna alla Homepage
              </Link>
              {articles.filter(a => a.slug !== slug).slice(0, 3).map((a) => (
                <Link
                  key={a.slug}
                  to={`/articolo/${a.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {a.tagLabel}
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </article>
    </>
  );
}